import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const MessageComposer = ({ currentLanguage, onSendMessage }) => {
  const [messageType, setMessageType] = useState('email');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [recipientType, setRecipientType] = useState('individual');
  const [selectedPilgrims, setSelectedPilgrims] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [scheduleMessage, setScheduleMessage] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const labels = {
    fr: {
      title: 'Nouveau message',
      messageType: 'Type de message',
      template: 'Modèle',
      selectTemplate: 'Sélectionner un modèle',
      subject: 'Objet',
      message: 'Message',
      recipients: 'Destinataires',
      individual: 'Individuel',
      group: 'Groupe',
      custom: 'Personnalisé',
      selectPilgrims: 'Sélectionner les pèlerins',
      selectGroups: 'Sélectionner les groupes',
      scheduleMessage: 'Planifier l\'envoi',
      scheduleDate: 'Date d\'envoi',
      scheduleTime: 'Heure d\'envoi',
      recipientCount: 'destinataires',
      send: 'Envoyer',
      schedule: 'Planifier',
      cancel: 'Annuler',
      preview: 'Aperçu'
    },
    ar: {
      title: 'رسالة جديدة',
      messageType: 'نوع الرسالة',
      template: 'قالب',
      selectTemplate: 'اختر قالباً',
      subject: 'الموضوع',
      message: 'الرسالة',
      recipients: 'المستلمون',
      individual: 'فردي',
      group: 'مجموعة',
      custom: 'مخصص',
      selectPilgrims: 'اختر الحجاج',
      selectGroups: 'اختر المجموعات',
      scheduleMessage: 'جدولة الإرسال',
      scheduleDate: 'تاريخ الإرسال',
      scheduleTime: 'وقت الإرسال',
      recipientCount: 'مستلم',
      send: 'إرسال',
      schedule: 'جدولة',
      cancel: 'إلغاء',
      preview: 'معاينة'
    }
  };

  const messageTypeOptions = [
    { value: 'email', label: currentLanguage === 'fr' ? 'Email' : 'بريد إلكتروني' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'sms', label: 'SMS' }
  ];

  const templateOptions = [
    { value: '', label: labels?.[currentLanguage]?.selectTemplate },
    { value: 'document_request', label: currentLanguage === 'fr' ? 'Demande de documents' : 'طلب وثائق' },
    { value: 'travel_reminder', label: currentLanguage === 'fr' ? 'Rappel de voyage' : 'تذكير بالسفر' },
    { value: 'visa_update', label: currentLanguage === 'fr' ? 'Mise à jour visa' : 'تحديث التأشيرة' },
    { value: 'payment_reminder', label: currentLanguage === 'fr' ? 'Rappel de paiement' : 'تذكير بالدفع' },
    { value: 'group_notification', label: currentLanguage === 'fr' ? 'Notification de groupe' : 'إشعار المجموعة' }
  ];

  const pilgrimOptions = [
    { value: 'p1', label: 'Ahmed Ben Ali - Groupe Omra Janvier 2026' },
    { value: 'p2', label: 'Fatima El Mansouri - Groupe Hajj 2026' },
    { value: 'p3', label: 'Mohammed Alaoui - Groupe Omra Février 2026' },
    { value: 'p4', label: 'Khadija Bennis - Groupe Omra Mars 2026' }
  ];

  const groupOptions = [
    { value: 'g1', label: 'Groupe Omra Janvier 2026 (45 pèlerins)' },
    { value: 'g2', label: 'Groupe Hajj 2026 (120 pèlerins)' },
    { value: 'g3', label: 'Groupe Omra Février 2026 (38 pèlerins)' }
  ];

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    
    const templates = {
      document_request: {
        fr: {
          subject: 'Documents requis pour votre voyage',
          content: 'Cher(e) pèlerin(e),\n\nNous vous rappelons que les documents suivants sont nécessaires pour finaliser votre dossier :\n- Copie du passeport\n- Photo d\'identité récente\n- Certificat de vaccination\n\nMerci de nous les transmettre dans les plus brefs délais.\n\nCordialement,\nL\'équipe OmraHajjManager'
        },
        ar: {
          subject: 'الوثائق المطلوبة لرحلتك',
          content: 'عزيزي الحاج/الحاجة،\n\nنذكركم بأن الوثائق التالية ضرورية لإتمام ملفكم:\n- نسخة من جواز السفر\n- صورة شخصية حديثة\n- شهادة التطعيم\n\nيرجى إرسالها في أقرب وقت ممكن.\n\nمع تحياتنا،\nفريق OmraHajjManager'
        }
      },
      travel_reminder: {
        fr: {
          subject: 'Rappel : Départ dans 7 jours',
          content: 'Cher(e) pèlerin(e),\n\nVotre départ approche ! Nous vous rappelons les informations importantes :\n- Date de départ : [DATE]\n- Heure de rendez-vous : [HEURE]\n- Lieu de rendez-vous : [LIEU]\n\nAssurez-vous d\'avoir tous vos documents.\n\nBon voyage !\nL\'équipe OmraHajjManager'
        },
        ar: {
          subject: 'تذكير: المغادرة خلال 7 أيام',
          content: 'عزيزي الحاج/الحاجة،\n\nاقترب موعد سفرك! نذكركم بالمعلومات المهمة:\n- تاريخ المغادرة: [التاريخ]\n- وقت الاجتماع: [الوقت]\n- مكان الاجتماع: [المكان]\n\nتأكد من حصولك على جميع وثائقك.\n\nرحلة موفقة!\nفريق OmraHajjManager'
        }
      }
    };

    const template = templates?.[value];
    if (template) {
      setSubject(template?.[currentLanguage]?.subject);
      setMessageContent(template?.[currentLanguage]?.content);
    }
  };

  const calculateRecipientCount = () => {
    if (recipientType === 'individual') {
      return selectedPilgrims?.length;
    } else if (recipientType === 'group') {
      const groupCounts = {
        'g1': 45,
        'g2': 120,
        'g3': 38
      };
      return selectedGroups?.reduce((total, groupId) => total + (groupCounts?.[groupId] || 0), 0);
    }
    return 0;
  };

  const handleSend = () => {
    const messageData = {
      type: messageType,
      subject,
      content: messageContent,
      recipientType,
      recipients: recipientType === 'individual' ? selectedPilgrims : selectedGroups,
      scheduled: scheduleMessage,
      scheduledDate: scheduleMessage ? scheduledDate : null,
      scheduledTime: scheduleMessage ? scheduledTime : null,
      timestamp: new Date()?.toISOString()
    };
    
    onSendMessage?.(messageData);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Send" size={20} className="text-primary" />
        </div>
        <h2 className="text-lg md:text-xl font-semibold text-foreground">
          {labels?.[currentLanguage]?.title}
        </h2>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label={labels?.[currentLanguage]?.messageType}
            options={messageTypeOptions}
            value={messageType}
            onChange={setMessageType}
            required
          />

          <Select
            label={labels?.[currentLanguage]?.template}
            options={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateChange}
          />
        </div>

        {messageType === 'email' && (
          <Input
            label={labels?.[currentLanguage]?.subject}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e?.target?.value)}
            placeholder={currentLanguage === 'fr' ? 'Entrez l\'objet du message' : 'أدخل موضوع الرسالة'}
            required
          />
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {labels?.[currentLanguage]?.message}
          </label>
          <textarea
            value={messageContent}
            onChange={(e) => setMessageContent(e?.target?.value)}
            rows={8}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder={currentLanguage === 'fr' ? 'Rédigez votre message...' : 'اكتب رسالتك...'}
            dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            {labels?.[currentLanguage]?.recipients}
          </label>
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => setRecipientType('individual')}
              className={`flex-1 px-4 py-3 rounded-md border transition-smooth ${
                recipientType === 'individual' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              <Icon name="User" size={18} className="inline mr-2" />
              {labels?.[currentLanguage]?.individual}
            </button>
            <button
              onClick={() => setRecipientType('group')}
              className={`flex-1 px-4 py-3 rounded-md border transition-smooth ${
                recipientType === 'group' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              <Icon name="Users" size={18} className="inline mr-2" />
              {labels?.[currentLanguage]?.group}
            </button>
          </div>

          {recipientType === 'individual' ? (
            <Select
              label={labels?.[currentLanguage]?.selectPilgrims}
              options={pilgrimOptions}
              value={selectedPilgrims}
              onChange={setSelectedPilgrims}
              multiple
              searchable
              required
            />
          ) : (
            <Select
              label={labels?.[currentLanguage]?.selectGroups}
              options={groupOptions}
              value={selectedGroups}
              onChange={setSelectedGroups}
              multiple
              searchable
              required
            />
          )}

          {calculateRecipientCount() > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Users" size={16} />
              <span>
                {calculateRecipientCount()} {labels?.[currentLanguage]?.recipientCount}
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <Checkbox
            label={labels?.[currentLanguage]?.scheduleMessage}
            checked={scheduleMessage}
            onChange={(e) => setScheduleMessage(e?.target?.checked)}
          />

          {scheduleMessage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label={labels?.[currentLanguage]?.scheduleDate}
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e?.target?.value)}
                required
              />
              <Input
                label={labels?.[currentLanguage]?.scheduleTime}
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e?.target?.value)}
                required
              />
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            variant="outline"
            iconName="Eye"
            iconPosition="left"
            className="flex-1"
          >
            {labels?.[currentLanguage]?.preview}
          </Button>
          <Button
            variant="default"
            iconName={scheduleMessage ? 'Clock' : 'Send'}
            iconPosition="left"
            onClick={handleSend}
            className="flex-1"
          >
            {scheduleMessage ? labels?.[currentLanguage]?.schedule : labels?.[currentLanguage]?.send}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;